<template>
    <CModal 
        title="Edit Artwork" 
        :show="show" 
        @update:show="$emit('update:show', $event)"
        size="lg"
        centered
        class="edit-artwork-modal"
    >
        <template #header>
            <div class="modal-header-custom w-100 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <div class="icon-square bg-red-light text-red mr-3">
                        <CIcon name="cil-pencil" size="lg" />
                    </div>
                    <div>
                        <h5 class="modal-title font-weight-bold">Edit Artwork</h5>
                        <p class="text-muted small mb-0">Modify the details of this artwork.</p>
                    </div>
                </div>
                <button type="button" class="close-btn" :aria-label="$i18n.locale === 'en' ? 'Close' : 'ปิด'" @click="$emit('update:show', false)">
                    <CIcon name="cil-x" size="sm" />
                </button>
            </div>
        </template>

        <div class="px-3 py-2">
            <!-- Rejection Reason Alert -->
            <div class="alert alert-danger d-flex align-items-center mb-4 rounded-xl border border-danger-subtle bg-danger-faded p-3" v-if="artwork && artwork.rejectionReason">
                <CIcon name="cil-warning" class="text-danger mr-3" size="lg" />
                <div>
                    <h6 class="alert-heading font-weight-bold mb-1 text-danger">{{ $i18n.locale === 'en' ? 'Reason for Rejection / Feedback' : 'เหตุผลที่ไม่ผ่านการประเมิน / ข้อเสนอแนะ' }}</h6>
                    <p class="mb-0 text-danger font-weight-bold">{{ artwork.rejectionReason }}</p>
                </div>
            </div>

            <!-- Basic Info Row -->
            <!-- Titles EN/TH -->
            <CRow>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Artwork Title (EN)</label>
                        <CInput 
                            v-model="form.title.en" 
                            placeholder="e.g. The Golden Serenity" 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Artwork Title (TH)</label>
                        <CInput 
                            v-model="form.title.th" 
                            placeholder="ชื่อผลงานภาษาไทย" 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
            </CRow>

            <!-- Category Row -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">Category</label>
                        <CDropdown :key="form.category" :caret="false" class="w-100">
                            <template #toggler>
                                <CButton class="custom-select-btn">
                                    <span class="text-dark">{{ selectedCategoryName }}</span>
                                    <CIcon name="cil-chevron-bottom" class="dropdown-icon" />
                                </CButton>
                            </template>
                            <div class="w-100 border-0 shadow-lg mt-2">
                                <CDropdownItem 
                                    v-for="cat in categories" 
                                    :key="cat._id" 
                                    @click="handleCategorySelect(cat._id)"
                                >
                                    {{ getName('en', cat) }}
                                </CDropdownItem>
                            </div>
                        </CDropdown>
                    </div>
                </CCol>
            </CRow>

            <!-- Storage Row -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">{{ $i18n.locale === 'en' ? 'Storage Location' : 'สถานที่จัดเก็บ' }}</label>
                        <CDropdown :key="form.storage" :caret="false" class="w-100">
                            <template #toggler>
                                <CButton class="custom-select-btn">
                                    <span class="text-dark">{{ selectedStorageName }}</span>
                                    <CIcon name="cil-chevron-bottom" class="dropdown-icon" />
                                </CButton>
                            </template>
                            <div class="w-100 border-0 shadow-lg mt-2">
                                <CDropdownItem 
                                    v-for="store in storages" 
                                    :key="store._id" 
                                    @click="handleStorageSelect(store._id)"
                                >
                                    {{ getName($i18n.locale, store) }}
                                </CDropdownItem>
                            </div>
                        </CDropdown>
                    </div>
                </CCol>
            </CRow>

            <!-- Technique & Status Row -->
            <!-- Technique Row EN/TH -->
            <CRow>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Technique (EN)</label>
                        <CInput 
                            v-model="form.technique.en" 
                            placeholder="e.g. Oil on Canvas" 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Technique (TH)</label>
                        <CInput 
                            v-model="form.technique.th" 
                            placeholder="เช่น สีน้ำมันบนผ้าใบ" 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
            </CRow>

            <!-- Size Row -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">Dimensions / ขนาด ({{ is3D ? 'กว้าง x ยาว x สูง' : 'กว้าง x ยาว' }})</label>
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <div class="input-group-custom">
                                    <span class="dimension-prefix">W</span>
                                    <CInput 
                                        type="number"
                                        v-model="form.width" 
                                        placeholder="Width" 
                                        class="custom-input-with-prefix mb-0"
                                    />
                                </div>
                            </div>
                            <div class="mx-2 text-muted font-weight-bold">×</div>
                            <div class="flex-grow-1">
                                <div class="input-group-custom">
                                    <span class="dimension-prefix">L</span>
                                    <CInput 
                                        type="number"
                                        v-model="form.height" 
                                        placeholder="Length" 
                                        class="custom-input-with-prefix mb-0"
                                    />
                                </div>
                            </div>
                            <template v-if="is3D">
                                <div class="mx-2 text-muted font-weight-bold">×</div>
                                <div class="flex-grow-1">
                                    <div class="input-group-custom">
                                        <span class="dimension-prefix">H</span>
                                        <CInput 
                                            type="number"
                                            v-model="form.depth" 
                                            placeholder="Height" 
                                            class="custom-input-with-prefix mb-0"
                                        />
                                    </div>
                                </div>
                            </template>
                            <div class="ml-2">
                                <CDropdown :key="form.unit" :caret="false">
                                    <template #toggler>
                                        <CButton class="custom-select-btn" style="min-width: 80px; height: 46px;">
                                            <span class="text-dark">{{ form.unit || 'Unit' }}</span>
                                            <CIcon name="cil-chevron-bottom" class="dropdown-icon ml-1" />
                                        </CButton>
                                    </template>
                                    <div class="border-0 shadow-lg mt-2">
                                        <CDropdownItem v-for="u in unitOptions" :key="u.en" @click="form.unit = u.en">
                                            {{ u.en }} ({{ u.th }})
                                        </CDropdownItem>
                                    </div>
                                </CDropdown>
                            </div>
                        </div>
                    </div>
                </CCol>
            </CRow>

            <!-- Remark Row -->
            <CRow>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Remark (EN)</label>
                        <CInput 
                            v-model="form.remark.en" 
                            placeholder="Additional notes in English..." 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
                <CCol md="6">
                    <div class="form-group-custom">
                        <label class="form-label">Remark (TH) / หมายเหตุ</label>
                        <CInput 
                            v-model="form.remark.th" 
                            placeholder="หมายเหตุเพิ่มเติม (ภาษาไทย)..." 
                            class="custom-input mb-0"
                        />
                    </div>
                </CCol>
            </CRow>

            <CRow>
                <CCol :md="isPriceVisible ? '6' : '12'">
                    <div class="form-group-custom">
                        <label class="form-label">Sale Status</label>
                        <CDropdown :key="form.status.sale" :caret="false" class="w-100">
                            <template #toggler>
                                <CButton class="custom-select-btn">
                                    <span class="text-dark">{{ selectedSaleStatusName }}</span>
                                    <CIcon name="cil-filter" class="dropdown-icon" />
                                </CButton>
                            </template>
                            <div class="w-100 border-0 shadow-lg mt-2">
                                <CDropdownItem 
                                    v-for="status in saleStatuses" 
                                    :key="status._id" 
                                    @click="handleSaleStatusSelect(status._id)"
                                >
                                    {{ getName('en', status) }}
                                </CDropdownItem>
                            </div>
                        </CDropdown>
                    </div>
                </CCol>
                <CCol md="6" v-if="isPriceVisible">
                    <div class="form-group-custom">
                        <label class="form-label">Price</label>
                        <div class="input-group-custom">
                            <span class="currency-prefix">฿</span>
                            <CInput 
                                type="number"
                                v-model="form.price" 
                                placeholder="0.00" 
                                class="custom-input-with-prefix text-right mb-0"
                            />
                        </div>
                    </div>
                </CCol>
                
            </CRow>

            <!-- File Upload Row -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">Artwork Image</label>
                        <div class="file-upload-container" :class="{ 'has-image': imagePreview }">
                            <input 
                                type="file" 
                                id="artwork-image-edit-upload" 
                                @change="handleFileUpload" 
                                accept="image/png,image/jpeg"
                                :disabled="isProcessingImage"
                                class="file-input-hidden"
                            />
                            <label for="artwork-image-edit-upload" class="file-upload-label w-100">
                                <template v-if="!imagePreview">
                                    <div class="upload-placeholder py-4 text-center">
                                        <CIcon name="cil-cloud-upload" size="xl" class="text-red mb-2" />
                                        <div class="font-weight-bold">Click to upload image</div>
                                        <div class="text-muted small">PNG, JPG or JPEG (Max 5MB)</div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="preview-container position-relative">
                                        <img :src="imagePreview" class="upload-preview" />
                                        <div class="preview-overlay">
                                            <div class="overlay-content">
                                                <CIcon name="cil-pencil" class="mr-2" /> Change Image
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </label>
                        </div>
                        <p v-if="imageError" role="alert" class="text-danger mt-2">{{ imageError }}</p>
                    </div>
                </CCol>
            </CRow>

            <!-- Description Row -->
            <!-- Description Row (EN) -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">Description (EN)</label>
                        <CTextarea 
                            v-model="form.description.en" 
                            placeholder="English description..." 
                            rows="4"
                            class="custom-textarea"
                        />
                    </div>
                </CCol>
            </CRow>

            <!-- Description Row (TH) -->
            <CRow>
                <CCol md="12">
                    <div class="form-group-custom">
                        <label class="form-label">Description (TH)</label>
                        <CTextarea 
                            v-model="form.description.th" 
                            placeholder="รายละเอียดภาษาไทย..." 
                            rows="4"
                            class="custom-textarea"
                        />
                    </div>
                </CCol>
            </CRow>
        </div>

        <template #footer>
            <div class="w-100 d-flex justify-content-end align-items-center py-2">
                <CButton 
                    variant="ghost" 
                    @click="$emit('update:show', false)"
                    class="btn-cancel mr-3"
                >
                    Cancel
                </CButton>
                <CButton 
                    color="primary" 
                    @click="handleSave"
                    class="btn-save-artwork"
                    :disabled="!isFormValid || loading"
                >
                    <CSpinner v-if="loading" size="sm" class="mr-2" />
                    {{ loading ? ($i18n.locale === 'en' ? 'Saving...' : 'กำลังบันทึก...') : ($i18n.locale === 'en' ? 'Save Changes' : 'บันทึกการแก้ไข') }}
                </CButton>
            </div>
        </template>
    </CModal>
</template>

<script>
import { mapGetters } from 'vuex';
import artistImageInput from '@/mixins/artistImageInput';

export default {
    mixins: [artistImageInput],
    name: 'ArtworkEditModal',
    components: {
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        artwork: {
            type: Object,
            default: () => null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            categories: [],
            statuses: [],
            storages: [],
            imagePreview: null,
            form: {
                id: null,
                title: { en: '', th: '' },
                artist: '',
                category: '',
                storage: '',
                technique: { en: '', th: '' },
                size: { en: '', th: '' },
                width: '',
                height: '',
                depth: '',
                unit: 'cm',
                price: '',
                description: { en: '', th: '' },
                status: {
                    approval: '',
                    sale: ''
                },
                remark: { en: '', th: '' },
                image: null
            },
            unitOptions: [
                { en: 'cm', th: 'ซม.' },
                { en: 'm', th: 'ม.' },
                { en: 'inch', th: 'นิ้ว' },
                { en: 'mm', th: 'มม.' }
            ]
        }
    },
    async created() {
        await this.fetchInitialData();
    },
    watch: {
        artwork: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.populateForm(newVal);
                }
            }
        },
        show(newVal) {
            if (newVal && this.artwork) {
                this.populateForm(this.artwork);
            }
        }
    },
    computed: {
        ...mapGetters({
            userId: 'auth/userId',
            allUsers: 'user/user'
        }),
        isFormValid() {
            const dimensions = [this.form.width, this.form.height, ...(this.is3D ? [this.form.depth] : [])];
            const basicValid = !this.isProcessingImage && this.form.title.en.trim() !== '' &&
                               this.form.title.th.trim() !== '' && 
                               this.form.artist !== '' && 
                               dimensions.every(value => Number.isFinite(Number(value)) && Number(value) > 0) &&
                               this.form.category !== '' && 
                               this.imagePreview !== null;
            
            if (this.isPriceVisible) {
                return basicValid && this.form.price !== '' && Number.isFinite(Number(this.form.price)) && Number(this.form.price) >= 0;
            }
            return basicValid;
        },
        saleStatuses() {
            if (!this.statuses) return [];
            return this.statuses.filter(s => {
                const groupName = s.group ? this.getName('en', s.group).trim() : '';
                const statusName = this.getName('en', s).trim();
                return groupName === 'Sale Status' && statusName === 'Available';
            });
        },
        approvalStatuses() {
            if (!this.statuses) return [];
            return this.statuses.filter(s => {
                const groupName = s.group ? this.getName('en', s.group).trim() : '';
                return groupName === 'Show Status';
            });
        },
        selectedCategoryName() {
            const cat = this.categories.find(c => String(c._id) === String(this.form.category));
            return cat ? this.getName('en', cat) : 'Select Category';
        },
        selectedStorageName() {
            const store = this.storages.find(s => String(s._id) === String(this.form.storage));
            return store ? this.getName(this.$i18n.locale, store) : (this.$i18n.locale === 'en' ? 'Select Storage Location' : 'เลือกสถานที่จัดเก็บ');
        },
        selectedSaleStatusName() {
            const status = this.saleStatuses.find(s => String(s._id) === String(this.form.status.sale));
            return status ? this.getName('en', status) : 'Select Status';
        },
        selectedApprovalStatusName() {
            const status = this.statuses.find(s => s._id === this.form.status.approval);
            return status ? this.getName('en', status) : 'Select Approval Status';
        },
        is3D() {
            const cat = this.categories.find(c => String(c._id) === String(this.form.category));
            if (!cat) return false;
            const enName = this.getName('en', cat).toLowerCase();
            const thName = this.getName('th', cat).toLowerCase();
            return enName.includes('sculpture') || 
                   enName.includes('installation') || 
                   thName.includes('ประติมากรรม') || 
                   thName.includes('จัดวาง');
        },
        isPriceVisible() {
            if (!this.form.status.sale) return false;
            return this.selectedSaleStatusName === 'Available';
        }
    },
    methods: {
        handleCategorySelect(id) {
            console.log('Category selected:', id);
            this.$set(this.form, 'category', id);
        },
        handleStorageSelect(id) {
            console.log('Storage selected:', id);
            this.$set(this.form, 'storage', id);
        },
        handleSaleStatusSelect(id) {
            console.log('Sale Status selected:', id);
            this.$set(this.form.status, 'sale', id);
        },
        async fetchInitialData() {
            try {
                this.$store.dispatch('user/get', {});

                const categories = await this.$store.dispatch('artwork/category', { method: 'get' });
                console.log('--- Edit Artwork Categories ---', categories);
                this.categories = categories || [];

                await this.$store.dispatch('setting/status/get');
                this.statuses = this.$store.getters['setting/status/item'] || [];
                console.log('--- Edit Artwork Statuses ---', this.statuses);

                // Fetch storage options
                await this.$store.dispatch('setting/storage/get');
                this.storages = this.$store.getters['setting/storage/item'] || [];
                console.log('--- Edit Artwork Storages ---', this.storages);
                
                if (this.artwork) {
                    console.log('--- Populating Form with Artwork ---', this.artwork);
                    this.populateForm(this.artwork);
                } else {
                    this.form.artist = this.userId;
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        },
        populateForm(artwork) {
            const getVal = (field, lang) => {
                if (!field) return '';
                if (typeof field === 'string') return field;
                if (Array.isArray(field)) {
                    const obj = field.find(n => n.key === lang);
                    return obj ? obj.value : '';
                }
                return '';
            };

            this.form = {
                id: artwork._id,
                title: {
                    en: getVal(artwork.title, 'en'),
                    th: getVal(artwork.title, 'th')
                },
                artist: artwork.artist ? (artwork.artist._id || artwork.artist) : (artwork.owner ? (artwork.owner._id || artwork.owner) : ''),
                category: artwork.category ? artwork.category._id : '',
                storage: artwork.storage ? (artwork.storage._id || artwork.storage) : '',
                technique: {
                    en: getVal(artwork.technique, 'en'),
                    th: getVal(artwork.technique, 'th')
                },
                size: {
                    en: getVal(artwork.size, 'en'),
                    th: getVal(artwork.size, 'th')
                },
                width: '',
                height: '',
                depth: '',
                unit: 'cm',
                price: this.cleanPrice(artwork.price),
                description: {
                    en: getVal(artwork.description, 'en'),
                    th: getVal(artwork.description, 'th')
                },
                status: {
                    approval: artwork.status?.approval?._id || artwork.status?._id || artwork.status || '',
                    sale: artwork.status?.sale?._id || this.statuses.find(status => status.code === 'AVAILABLE' || this.getName('en', status) === 'Available')?._id || ''
                },
                remark: {
                    en: getVal(artwork.remark, 'en'),
                    th: getVal(artwork.remark, 'th')
                },
                image: null
            };
            this.imagePreview = artwork.image || null;

            // Parse size string
            const sizeEn = getVal(artwork.size, 'en') || '';
            const sizeParts = sizeEn.split('x').map(p => p.trim());
            
            if (sizeParts.length >= 3) {
                // Handle 3D: W x L x H Unit
                this.form.width = sizeParts[0].replace(/[^0-9.]/g, '');
                this.form.height = sizeParts[1].replace(/[^0-9.]/g, '');
                
                const heightWithUnit = sizeParts[2].split(' ');
                this.form.depth = heightWithUnit[0].replace(/[^0-9.]/g, '');
                this.form.unit = heightWithUnit[1] || 'cm';
            } else if (sizeParts.length >= 2) {
                // Handle 2D: W x H Unit
                this.form.width = sizeParts[0].replace(/[^0-9.]/g, '');
                const heightWithUnit = sizeParts[1].split(' ');
                this.form.height = heightWithUnit[0].replace(/[^0-9.]/g, '');
                this.form.depth = '';
                this.form.unit = heightWithUnit[1] || 'cm';
            } else {
                this.form.width = '';
                this.form.height = '';
                this.form.depth = '';
                this.form.unit = 'cm';
            }
        },
        cleanPrice(price) {
            if (price === null || price === undefined || price === '') return '';
            return String(price).replace(/[^0-9.]/g, '');
        },
        getUnitTH(unitEN) {
            const unit = this.unitOptions.find(u => u.en === unitEN);
            return unit ? unit.th : unitEN;
        },
        handleSave() {
            if (!this.isFormValid) return;
            
            // Find the "Pending" status to reset approval upon edit
            const pendingStatus = this.approvalStatuses.find(s => this.getName('en', s) === 'Pending');
            if (pendingStatus) {
                this.form.status.approval = pendingStatus._id;
            }

            const payload = {
                _id: this.form.id,
                title: [{ key: 'en', value: this.form.title.en }, { key: 'th', value: this.form.title.th }],
                description: [{ key: 'en', value: this.form.description.en }, { key: 'th', value: this.form.description.th }],
                artist: this.form.artist,
                category: this.form.category,
                storage: this.form.storage || null,
                technique: [{ key: 'en', value: this.form.technique.en }, { key: 'th', value: this.form.technique.th }],
                size: [
                    { 
                        key: 'en', 
                        value: this.is3D 
                            ? `${this.form.width} x ${this.form.height} x ${this.form.depth} ${this.form.unit}` 
                            : `${this.form.width} x ${this.form.height} ${this.form.unit}` 
                    },
                    { 
                        key: 'th', 
                        value: this.is3D 
                            ? `${this.form.width} x ${this.form.height} x ${this.form.depth} ${this.getUnitTH(this.form.unit)}` 
                            : `${this.form.width} x ${this.form.height} ${this.getUnitTH(this.form.unit)}` 
                    }
                ],
                price: this.isPriceVisible ? parseFloat(this.form.price) : 0,
                status: this.form.status,
                remark: [{ key: 'en', value: this.form.remark.en }, { key: 'th', value: this.form.remark.th }],
                _image: this.form.image
            };

            this.$emit('save', payload);
        }
    }
}
</script>

<style scoped>
/* Reusing styles from ArtworkCreateModal.vue for consistency */
.form-group-custom {
    margin-bottom: 1.25rem;
}

.form-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 0.75rem;
    letter-spacing: 0.05em;
}

.custom-input ::v-deep .form-control,
.custom-textarea ::v-deep .form-control {
    border-radius: 12px;
    border: 1px solid #f1f5f9;
    padding: 0.85rem 1.25rem;
    height: auto;
    font-size: 1rem;
    transition: all 0.2s ease;
    background-color: #f8fafc;
    color: #1e293b;
}

.custom-input ::v-deep .form-control:focus,
.custom-textarea ::v-deep .form-control:focus {
    border-color: #8c151533;
    background-color: #ffffff;
    box-shadow: 0 0 0 4px rgba(140, 21, 21, 0.05);
}

.input-group-custom {
    position: relative;
}

.currency-prefix {
    position: absolute;
    left: 18px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 600;
    color: #1e293b;
    z-index: 10;
    font-size: 1.1rem;
    pointer-events: none;
}

.custom-input-with-prefix ::v-deep .form-control {
    padding-left: 2.85rem !important;
    height: 52px !important;
    border-radius: 12px !important;
    border: 1px solid #f1f5f9 !important;
    background-color: #f8fafc !important;
    font-size: 1rem !important;
}

.custom-input-with-prefix ::v-deep .form-control:focus {
    background-color: #ffffff !important;
    border-color: #8c151533 !important;
    box-shadow: 0 0 0 4px rgba(140, 21, 21, 0.05) !important;
}

.dimension-prefix {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 700;
    color: #94a3b8;
    font-size: 0.7rem;
    z-index: 10;
}

.custom-input-with-prefix ::v-deep .form-control {
    padding-left: 2.2rem !important;
}

.custom-select-btn {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 0.85rem 1.25rem;
    height: auto;
    font-size: 1rem;
    transition: all 0.2s ease;
    outline: none;
    cursor: pointer;
}

.custom-select-btn:hover {
    background: #ffffff;
    border-color: #e2e8f0;
}

.dropdown-icon {
    color: #94a3b8;
    width: 14px;
}

/* File Upload Stying */
.file-upload-container {
    border: 2px dashed #e2e8f0;
    border-radius: 16px;
    background-color: #f8fafc;
    transition: all 0.2s ease;
    overflow: hidden;
}

.file-upload-container:hover {
    border-color: #8c1515;
    background-color: #f1f5f9;
}

.file-input-hidden {
    display: none;
}

.file-upload-label {
    cursor: pointer;
    display: block;
    margin-bottom: 0;
}

.preview-container {
    width: 100%;
    height: 180px;
}

.upload-preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #ffffff;
}

.preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(30, 41, 59, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
}

.preview-container:hover .preview-overlay {
    opacity: 1;
}

.overlay-content {
    color: white;
    font-weight: 600;
    display: flex;
    align-items: center;
}

/* Header & Modal Stying */
.icon-square {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bg-red-light {
    background-color: #8c151511;
}

.text-red {
    color: #8c1515;
}

.close-btn {
    background: transparent;
    border: none;
    padding: 8px;
    color: #94a3b8;
    transition: color 0.2s;
}

.close-btn:hover {
    color: #64748b;
}

::v-deep .modal-content {
    border-radius: 20px;
    border: none;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
}

::v-deep .modal-header {
    border-bottom: 1px solid #f1f5f9;
    padding: 1.5rem 2rem;
}

::v-deep .modal-footer {
    border-top: 1px solid #f1f5f9;
    padding: 1.5rem 2.5rem;
}

.btn-cancel {
    color: #94a3b8 !important;
    font-weight: 600;
    font-size: 1rem;
}

.btn-save-artwork {
    background-color: #8c1515 !important;
    border-color: #8c1515 !important;
    color: white !important;
    border-radius: 30px !important;
    padding: 0.8rem 2.5rem !important;
    font-weight: 600 !important;
    font-size: 1rem;
    box-shadow: 0 10px 15px -3px rgba(140, 21, 21, 0.2);
}

</style>
